<template>
  <v-dialog :model-value="modelValue" @update:model-value="$emit('update:modelValue', $event)" max-width="600"
    persistent transition="dialog-bottom-transition">
    <v-card rounded="lg" class="overflow-hidden">

      <!-- Header -->
      <div class="card-section-header">
        <div class="d-flex align-center ga-3">
          <v-avatar color="brand-color" variant="tonal" size="40">
            <v-icon color="brand-color">mdi-bookshelf</v-icon>
          </v-avatar>
          <h2 class="section-title">Quick Start Guide</h2>
        </div>
      </div>

      <!-- Content -->
      <v-card-text class="px-6 pb-3 overflow-y-scroll">
        <v-window v-model="activeTab" :touch="false">

          <!-- 1. Overview -->
          <v-window-item value="overview">
            <v-chip color="accent-color" variant="tonal" size="small" rounded="lg" label class="mb-4 font-weight-bold">
              👋 Welcome aboard
            </v-chip>
            <h5 class="section-title mb-3">
              Great to have you here —<br>
              <span class="text-accent-color">let's get you set up.</span>
            </h5>
            <p class="body-text-sm mb-2">
              {{ overview.typedText }}<span v-show="overview.isTyping" class="typing-cursor">|</span>
            </p>
          </v-window-item>

          <!-- 2. Quick Guide -->
          <v-window-item value="quick-guide" class="bg-transparent">
            <p class="body-text-sm mb-2">Watch our quick guide walkthrough to help you get started.</p>
            <v-card rounded="lg" variant="outlined" class="overflow-hidden mt-2">
              <v-responsive :aspect-ratio="16 / 9" class="position-relative bg-black">
                <!-- Poster on top via z-index, v-show keeps it in DOM — no removal flash -->
                <div
                  :class="['position-absolute w-100 h-100 d-flex align-center justify-center iframe-poster', { 'iframe-poster-hidden': video.ready }]">
                  <v-img :src="require('@/assets/images/auth/video-bg.jpg')" cover
                    class="w-100 h-100 position-absolute" />
                  <v-progress-circular indeterminate color="brand-color" size="32" width="2"
                    class="position-relative" />
                </div>
                <!-- iframe fades in under poster; poster hides only after fade completes -->
                <iframe v-if="video.visited" :src="video.url" width="100%" height="100%" frameborder="0"
                  referrerpolicy="unsafe-url" allowfullscreen allow="clipboard-write"
                  sandbox="allow-popups allow-popups-to-escape-sandbox allow-scripts allow-forms allow-same-origin allow-presentation"
                  class="iframe-embed" :class="{ 'iframe-visible': video.loaded }" @load="onIframeLoad" />
              </v-responsive>
            </v-card>
          </v-window-item>

          <!-- 3. Help Centre -->
          <v-window-item value="help-centre" class="tab-3">
            <p class="body-text-sm mb-0">
              Our Help Centre has step-by-step guides, video walkthroughs, and FAQs for every
              feature in <b class="text-accent-color">CliqForms</b>. If you ever get stuck, you'll find the answer
              there.
            </p>
            <v-list class="mt-3" base-color="brand-color" @click="openHelpUrl(helpArticles.HELP_CENTER)">
              <v-list-item title="Visit the Help Centre"
                subtitle="Guides, walkthroughs & FAQs for every CliqForms feature." variant="tonal" rounded="lg"
                lines="two" class="cursor-pointer">
                <template #prepend>
                  <v-avatar color="brand-color" size="35" rounded="lg">
                    <v-icon size="20" color="white">mdi-help-circle-outline</v-icon>
                  </v-avatar>
                </template>
                <template v-slot:append>
                  <v-avatar color="brand-color" size="20" class="hover-class">
                    <v-icon size="10" color="white">mdi-chevron-right</v-icon>
                  </v-avatar>
                </template>
              </v-list-item>
            </v-list>
          </v-window-item>

        </v-window>

      </v-card-text>

      <!-- Tab Buttons -->
      <v-card-text v-if="!isVideoOnlyMode" class="d-flex flex-wrap ga-2 px-6 pt-0 pb-4">
        <v-btn v-for="tab in TABS" :key="tab.value" size="small" variant="tonal" rounded="lg" :prepend-icon="tab.icon"
          :color="tab.value === activeTab ? 'brand-color' : 'outlined-color'"
          @click="userNavigated = true; activeTab = tab.value">
          {{ tab.title }}
        </v-btn>
      </v-card-text>

      <!-- Actions -->
      <v-divider />
      <v-card-actions class="px-5 pb-5 pt-4">
        <v-btn color="outlined-color" variant="text" class="text-none text-medium-emphasis font-weight-medium ml-n4"
          @click="dismiss">
          Skip
        </v-btn>
        <v-spacer />
        <v-btn color="brand-color" variant="flat" rounded="lg" class="text-none font-weight-bold px-5"
          append-icon="mdi-arrow-right" @click="dismiss">
          Got it, let's go!
        </v-btn>
      </v-card-actions>

    </v-card>
  </v-dialog>
</template>

<script>
import { helpArticles } from '@/shared/constants/help-articles'
import utilityMixin from '@/mixins/utility'

const TABS = [
  { value: 'overview', title: 'Overview', icon: 'mdi-numeric-1-circle' },
  { value: 'quick-guide', title: 'Quick Guide', icon: 'mdi-numeric-2-circle' },
  { value: 'help-centre', title: 'Help Centre', icon: 'mdi-numeric-3-circle' }
]

const WORDS = "CliqForms is your all-in-one platform for collecting data, capturing leads, getting approvals, and turning intent into action. Built specifically for SMEs - powerful enough for the important stuff, simple enough that you'll use it every day."
  .split(/\s+/)

export default {
  name: 'QuickStartGuide',
  mixins: [utilityMixin],

  props: {
    modelValue: { type: Boolean, default: false },
    entryMode: { type: String, default: 'full' }
  },
  emits: ['update:modelValue', 'close'],

  data: () => ({
    helpArticles,
    TABS,
    activeTab: TABS[0].value,
    userNavigated: false,
    _t: null,
    overview: {
      typedText: '',
      isTyping: false
    },
    video: {
      loaded: false,  // iframe @load fired - start fade in
      ready: false,   // fade complete - remove poster
      visited: false,
      url: helpArticles?.QUICK_START_GUIDDE_EMBED
    }
  }),
  computed: {
    isVideoOnlyMode() {
      return this.entryMode === 'video-only'
    }
  },

  watch: {
    modelValue(open) {
      if (open) {
        this.initializeForOpen()
      } else this.stop()
    },
    activeTab(val) {
      if (val === TABS[1].value) {
        this.video.visited = true
      }
    }
  },

  mounted() {
    if (this.modelValue) this.initializeForOpen()
  },
  beforeUnmount() { this.stop() },

  methods: {
    stop() { clearTimeout(this._t); this.overview.isTyping = false },
    initializeForOpen() {
      this.stop()
      this.userNavigated = false
      this.video.loaded = false
      this.video.ready = false
      this.video.visited = false

      if (this.isVideoOnlyMode) {
        this.overview.typedText = ''
        this.activeTab = TABS[1].value
        this.video.visited = true
        return
      }

      this.activeTab = TABS[0].value
      this.startTyping()
    },

    startTyping() {
      this.stop()
      this.overview.typedText = ''
      this.overview.isTyping = true
      let i = 0
      const tick = () => {
        this.overview.typedText = WORDS.slice(0, ++i).join(' ')
        if (i >= WORDS.length) {
          this.stop()
          if (!this.userNavigated) {
            this._t = setTimeout(() => { this.activeTab = TABS[1].value }, 3000)
          }
          return
        }
        this._t = setTimeout(tick, /[.!?,]$/.test(WORDS[i - 1]) ? 110 : 55)
      }
      tick()
    },

    onIframeLoad() {
      this.video.loaded = true
      setTimeout(() => {
        this.video.ready = true
      }, 2500)
    },

    dismiss() { this.stop(); this.$emit('update:modelValue', false); this.$emit('close') }
  }
}
</script>

<style scoped>
.typing-cursor {
  animation: blink 0.7s step-end infinite
}

@keyframes blink {
  50% {
    opacity: 0
  }
}

.iframe-poster {
  z-index: 1;
  transition: opacity 0.4s ease;
}

.iframe-poster-hidden {
  opacity: 0;
  pointer-events: none;
}

.iframe-embed {
  opacity: 0;
  transition: opacity 0.4s ease
}

.iframe-visible {
  opacity: 1
}

:deep(.tab-3 .v-list-item-title) {
  font-family: var(--heading-font-stack) !important;
  font-weight: 600 !important;
  font-size: 14px !important;
  line-height: 1.4 !important;
}

:deep(.tab-3 .v-list-item-subtitle) {
  font-family: var(--text-font-stack) !important;
  font-weight: 500 !important;
  font-size: 12px !important;
  line-height: 1.4 !important;
}

:deep(.hover-class:hover) {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  transform: translateY(-1px);
  transition: all 0.2s ease;
}
</style>
